import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { Field, getFormValues, reduxForm } from 'redux-form';
import translate from 'components/hoc/translate';
import { ButtonWrapper, FormWrapper, Item, Row, Section } from 'components/ui/table/elements';
import EnhancedRaisedButton from 'components/button/EnhancedRaisedButton';
import validations from 'helpers/forms/validations';
import RenderTextInputField from 'components/form/InputFields/RenderTextInputField';
import RenderPriceInputField from 'components/form/InputFields/RenderPriceInputField';
import RenderPercentageInputField from 'components/form/InputFields/RenderPercentageInputField';
import RenderAutoCompleteInputField from 'components/form/InputFields/RenderAutoCompleteInputField';
import labeledCountries from 'helpers/countriesHelper';
import RenderDateInputField from 'components/form/InputFields/RenderDateInputField';
import currencyHelper from 'helpers/currencyHelper';
import SelectField from 'components/input/SelectField/SelectField';
import { filter, isEmpty, map, toNumber } from 'lodash';
import RenderProductInputField from 'components/form/InputFields/AutoCompleteField/RenderProductInputField';
import ProductMembershipInputField from './ProductMembershipInputField';
import { connect } from 'react-redux';
import { requiresLicensePlate } from 'helpers/product';
import GenerateSelectField from 'components/input/SelectField/GenerateSelectField';
import DefaultRadioButtonGroupField from 'components/form/InputFields/DefaultRadioButtonGroupField';
import { fetchProductMembership } from 'store/actions/product-memberships/fetchProductMemberships';
import utils from 'helpers/utils';
import RenderCheckBoxInputField from 'components/form/InputFields/RenderCheckBoxInputField';
import RenderAutoCompleteDataInputField from 'components/form/InputFields/AutoCompleteField/RenderAutoCompleteDataInputField';

const defaultScheduleOptions = [
    { label: 'Schedule', value: '1' },
    { label: 'On-Demand', value: '0' },
];

class UserProductMembershipForm extends React.Component {
    constructor(props) {
        super(props);

        this.setOptions = this.setOptions.bind(this);

        this.state = {
            options: [
                ...defaultScheduleOptions,
            ],
            showVehicle: false,
        };
    }

    componentDidUpdate(prevProps) {
        const {
            change,
            productMembership,
            values,
            values: {
                product_id = null,
                product_membership_id = null,
            },
            product,
        } = this.props;

        const diffValues = utils.diffObjects(prevProps.values, values);
        const diffProductMembership = utils.diffObjects(prevProps.productMembership, productMembership);

        if (product_membership_id && product_membership_id !== prevProps.values.product_membership_id) {
            return this.props.dispatchFetchProductMembership(product_membership_id);
        }

        if (!isEmpty(diffProductMembership) && (!diffProductMembership.supports_ondemand || !diffProductMembership.supports_scheduled)) {
            return this.setOptions(productMembership);
        }

        if (!isEmpty(diffValues) && diffValues.product_id && values.product_membership_id) {
            return change('product_id', product_id);
        }

        if (!isEmpty(diffValues) && diffValues.is_scheduled !== undefined) {
            return change('price', diffValues.is_scheduled ? productMembership.price_scheduled : productMembership.price_ondemand);
        }

        if (product) {
            if (product.id !== prevProps.product.id) {
                this.checkVehicleRequired();
            }
        }
    }

    checkVehicleRequired = () => {
        const {
            product,
        } = this.props;

        const showVehicle = requiresLicensePlate(product);

        this.setState({
            showVehicle,
        });
    }

    setOptions = (productMembership) => {
        const {
            initialize,
        } = this.props;

        this.setState(() => {
            const options = [
                ...defaultScheduleOptions,
            ];

            if (productMembership && !productMembership.supports_ondemand) {
                options.pop();
            }

            if (productMembership && !productMembership.supports_scheduled) {
                options.shift();
            }

            return {
                options,
            };
        }, () => {
            if (!productMembership.supports_scheduled || !productMembership.supports_ondemand) {
                initialize({
                    ...this.props.values,
                    price: productMembership.supports_scheduled ? productMembership.price_scheduled : productMembership.price_ondemand,
                    is_scheduled: +productMembership.supports_scheduled,
                });
            }
        });
    }

    render() {
        const {
            handleSubmit,
            pristine,
            submitting,
            translate,
            productMembership,
        } = this.props;

        const {
            options,
            showVehicle,
        } = this.state;

        return (
            <FormWrapper>
                <form onSubmit={ handleSubmit }>
                    <Section title={ translate('resources.subscriptionProducts.fields.product') }>
                        <Row>
                            <Item>
                                <Field
                                    component={ RenderProductInputField }
                                    fullWidth={ true }
                                    name='product_id'
                                    label={ translate('resources.subscriptionProducts.fields.product') }
                                    validate={ validations.required }
                                />
                            </Item>
                        </Row>
                        <Row>
                            <Item>
                                <Field
                                    component={ ProductMembershipInputField }
                                    name='product_membership_id'
                                    label={ translate('resources.product_memberships.fields.product_membership_id') }
                                    validate={ validations.required }
                                />
                            </Item>
                        </Row>
                        <Row>
                            <Item>
                                <Field
                                    component={ RenderAutoCompleteDataInputField }
                                    fullWidth={ true }
                                    floatingLabelFixed={ true }
                                    name='user_product_membership_pool_id'
                                    label={ translate('resources.product_memberships.fields.user_product_membership_pool_id') }
                                    optionValue='id'
                                    optionText='name'
                                    reference='user-product-membership-pools'
                                    searchBy='name'
                                    searchParams={ {
                                        size: 12,
                                        sort_by: 'name',
                                        sort_direction: 'asc',
                                    } }
                                    validate={ [
                                        validations.required,
                                    ] }
                                />
                            </Item>
                        </Row>
                        <Row>
                            <Item>
                                <Field
                                    choices={ this.props.groupIdentfiers }
                                    component={ GenerateSelectField }
                                    fullWidth={ true }
                                    floatingLabelFixed={ true }
                                    name='group_identifier'
                                    label={ translate('resources.product_memberships.fields.group_identifier') }
                                    validate={ [
                                        validations.required,
                                    ] }
                                />
                            </Item>
                        </Row>
                        <Row>
                            <Item>
                                <Field
                                    component={ RenderAutoCompleteInputField }
                                    floatingLabelFixed={ true }
                                    fullWidth={ true }
                                    label={ translate('resources.accounts.fields.country') }
                                    name='country_iso2'
                                    choices={ labeledCountries }
                                    optionValue='ISO2'
                                    optionText={ (choice) => {
                                        return translate(choice.name);
                                    } }
                                />
                            </Item>
                        </Row>
                        {
                            showVehicle &&
                            (
                                <Row>
                                    <Item>
                                        <Field
                                            component={ RenderTextInputField }
                                            fullWidth={ true }
                                            floatingLabelFixed={ true }
                                            name='user_vehicle_id'
                                            label={ translate('resources.product_memberships.fields.user_vehicle_id') }
                                            validate={ [
                                                validations.number,
                                            ] }
                                        />
                                    </Item>
                                </Row>
                            )
                        }

                    </Section>

                    <Section title={ translate('fields.dates') }>
                        <Row>
                            <Item>
                                <Field
                                    component={ RenderDateInputField }
                                    floatingLabelFixed={ true }
                                    fullWidth={ false }
                                    name='starts_at'
                                    label={ translate('fields.starts_at') }
                                    validate={ validations.required }
                                />
                            </Item>
                        </Row>
                        <Row>
                            <Item>
                                <Field
                                    component={ RenderDateInputField }
                                    floatingLabelFixed={ true }
                                    fullWidth={ false }
                                    name='ends_at'
                                    label={ translate('fields.ends_at') }
                                />
                            </Item>
                        </Row>
                    </Section>
                    <Section title={ translate('resources.products.forms.preferences') }>
                        <Row>
                            <Item>
                                <Field
                                    component={ DefaultRadioButtonGroupField }
                                    fullWidth={ false }
                                    name='is_scheduled'
                                    label={ translate('fields.is_scheduled') }
                                    options={ options }
                                    normalize={ (value) => {
                                        return (value === null) ? null : !!+value; // Output null, true or false
                                    } }
                                    validate={ validations.required }
                                />
                            </Item>
                        </Row>
                    </Section>
                    <Section title={ translate('resources.users.memberships.pricing') }>
                        <label>{ translate('resources.product_memberships.create.pricing') }</label>
                        {
                            productMembership ?
                                <div>
                                    <p>{ productMembership.supports_ondemand ? `${ translate('resources.product_memberships.fields.price_ondemand') } : ${ productMembership.price_ondemand }` : null }</p>
                                    <p>{ productMembership.supports_scheduled ? `${ translate('resources.product_memberships.fields.price_scheduled') } : ${ productMembership.price_scheduled }` : null }</p>
                                </div>
                                : null
                        }
                        <Row>
                            <Item>
                                <Field
                                    choices={ currencyHelper.currencyChoices }
                                    component={ SelectField }
                                    floatingLabelFixed={ true }
                                    label={ translate('fields.currency') }
                                    optionText='name'
                                    optionValue='code'
                                    name='currency'
                                    validate={ validations.required }
                                />
                            </Item>
                        </Row>
                        <Row>
                            <Item>
                                <Field
                                    component={ RenderPriceInputField }
                                    fullWidth={ false }
                                    name='price'
                                    label={ translate('fields.price') }
                                    validate={ validations.number }
                                />
                            </Item>
                        </Row>
                        <Row>
                            <Item>
                                <Field
                                    component={ RenderPercentageInputField }
                                    format={ (value) => {
                                        return value ? (toNumber(value) * 100) : value;
                                    } }
                                    fullWidth={ false }
                                    name='vat_percentage'
                                    normalize={ (value) => {
                                        return value ? (toNumber(value) / 100) : value;
                                    } }
                                    label={ translate('resources.products.fields.vat_percentage') }
                                />
                            </Item>
                        </Row>
                    </Section>
                    <Section>
                        <label>{ translate('resources.product_memberships.fields.payment_method_description') }</label>
                        <Row>
                            <Item>
                                <Field
                                    component={ RenderCheckBoxInputField }
                                    fullWidth={ true }
                                    floatingLabelFixed={ true }
                                    name='payment_method'
                                    label={ translate('resources.product_memberships.fields.deferred') }
                                    type='checkbox'
                                />
                            </Item>
                        </Row>
                    </Section>

                    <Section>
                        <ButtonWrapper>
                            <EnhancedRaisedButton
                                label={ translate('actions.submit') }
                                type='submit'
                                loading={ submitting }
                                disabled={ submitting || pristine }
                                primary
                            />
                        </ButtonWrapper>
                    </Section>
                </form>
            </FormWrapper>
        );
    }
}

UserProductMembershipForm.propTypes = {
    change: PropTypes.func.isRequired,
    dispatchFetchProductMembership: PropTypes.func,
    groupIdentfiers: PropTypes.array,
    handleSubmit: PropTypes.func.isRequired,
    pristine: PropTypes.bool.isRequired,
    productMembership: PropTypes.shape({
        currency: PropTypes.string,
        price: PropTypes.number,
        price_ondemand: PropTypes.number,
        price_scheduled: PropTypes.number,
        supports_ondemand: PropTypes.bool,
        supports_scheduled: PropTypes.bool,
    }),
    initialValues: PropTypes.shape({
        product_membership_id: PropTypes.number,
    }),
    initialize: PropTypes.func.isRequired,
    submitting: PropTypes.bool.isRequired,
    translate: PropTypes.func.isRequired,
    values: PropTypes.shape({
        currency: PropTypes.string,
        ends_at: PropTypes.string,
        group_identifier: PropTypes.string,
        is_scheduled: PropTypes.oneOfType([
            PropTypes.bool,
            PropTypes.string,
        ]),
        price: PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.string, // Input field values type
        ]),
        product_id: PropTypes.string,
        product_membership_id: PropTypes.number,
        starts_at: PropTypes.string,
        user_id: PropTypes.number,
        user_vehicle_id: PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.string, // Input field values type
        ]),
        vat_percentage: PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.string, // Default value for precentage-input,
        ]),
    }),
    product: PropTypes.object,
};

const mapStateToProps = (state, { form, initialValues }) => {
    const values = getFormValues(form)(state);
    const { data: productResource } = state.products;
    const { data: productMembershipsResource, loading: loadingProductMemberships } = state['product-memberships'];

    const groupIdentfiers = map(
        filter(state.userProductMemberships.data, { user_id: initialValues.user_id }),
        (membership) => {
            return {
                id: membership.group_identifier,
                name: membership.group_identifier,
            };
        }
    );

    const productId = productMembershipsResource[values.product_membership_id] ?
        productMembershipsResource[values.product_membership_id].product_id :
        values.product_id;

    const mappedProps = {
        groupIdentfiers,
        productMembership: productMembershipsResource[values.product_membership_id] || null,
        values: {
            ...values,
            product_id: productId,
        },
        loadingProductMemberships,
        product: productResource[values.product_id] || {},
    };

    return mappedProps;
};

const mapDispatchToProps = (dispatch) => {
    return {
        dispatchFetchProductMembership: (id) => {
            dispatch(fetchProductMembership({ id }));
        },
    };
};

const enhance = compose(
    translate,
    reduxForm(),
    connect(
        mapStateToProps,
        mapDispatchToProps,
    ),
);

export default enhance(UserProductMembershipForm);



// WEBPACK FOOTER //
// ./src/pages/users/product-memberships/UserProductMembershipForm.js